// src/views/admin/ADReinscriptionMasse.jsx
//
// Outil de clôture d'année / réinscription en masse. Conçu pour l'onglet
// "Réinscription" de la page de gestion des élèves (ADEleves.jsx).
//
// LOGIQUE DE SÉLECTION AUTOMATIQUE (années) :
// - CIBLE (nouvelle année) = l'année ACTIVE.
// - SOURCE (année précédente) = l'autre année non archivée, différente
//   de la cible.
// Chaque champ peut être surchargé manuellement par l'admin ; dans ce cas
// l'auto-suggestion correspondante s'arrête tant que l'autre champ ne
// change pas.
//
// GESTION DU TYPE D'ÉLÈVE :
// - Obligatoire pour chaque réinscription (détermine les frais de
//   scolarité — RG2/EngagementFrais côté backend).
// - Sélecteur "pour le groupe" (comme pour la classe cible), avec
//   pré-remplissage par ligne à partir du type d'élève de l'année source.
// - Colonne "Scolarité" par élève, calculée dès que Classe cible + Type
//   d'élève sont choisis pour la ligne.
//
// SÉLECTION DES ÉLÈVES :
// - Aucun élève n'est précoché à l'ouverture d'une classe — l'admin
//   choisit explicitement qui réinscrire (évite les réinscriptions
//   accidentelles par simple oubli de décocher un élève qu'on ne
//   voulait pas traiter).
//
// TABLEAU : colonnes à largeur fixe (table-layout: fixed) pour que la
// case à cocher reste petite, le nom/prénom lisible sans excès, et les
// select (classe cible / type élève) bien cadrés avec leur bordure
// entièrement visible dans leur cellule.
//
// Props : { slug }
import { useState, useEffect, useMemo } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { urlimage } from "../../utils/config";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${urlimage}${path}`;
  return `${urlimage}/${path}`;
};

export default function ADReinscriptionMasse({ slug }) {
  const toast = useToastHook();
  const adminService = useAdminService();
  const {
    getAnneesScolaires, getClasses, getAffectationsFrais,
    getReinscriptionClasses, getReinscriptionElevesClasse, validerReinscription,
  } = adminService;
  const getTypesEleveFn = adminService.getTypesEleves;

  const [loading, setLoading] = useState(true);
  const [annees, setAnnees] = useState([]);
  const [anneeSourceId, setAnneeSourceId] = useState("");
  const [anneeCibleId, setAnneeCibleId] = useState("");
  const [sourceModifieeManuellement, setSourceModifieeManuellement] = useState(false);
  const [cibleModifieeManuellement, setCibleModifieeManuellement] = useState(false);
  const [typesEleve, setTypesEleve] = useState([]);

  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [classeSource, setClasseSource] = useState(null);
  const [eleves, setEleves] = useState([]);
  const [elevesLoading, setElevesLoading] = useState(false);
  const [classesCible, setClassesCible] = useState([]);

  const [classeCibleGroupe, setClasseCibleGroupe] = useState("");
  const [typeEleveGroupe, setTypeEleveGroupe] = useState("");
  const [lignes, setLignes] = useState({}); // eleve_id -> { selectionne, classe_cible_id, type_eleve_id, is_redoublant, exclu }
  const [afficherDejaTraites, setAfficherDejaTraites] = useState(false);
  const [saving, setSaving] = useState(false);

  // ─── Cache des totaux de scolarité par couple "classe-type_eleve" ───────
  const [totauxCache, setTotauxCache] = useState({}); // "classeId-typeId" -> montant (number) | null (erreur)

  // ─── CHARGEMENT DES ANNÉES + TYPES D'ÉLÈVES ─────────────────────────────
  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      try {
        const data = await getAnneesScolaires(slug, { show_archived: 'true' });
        setAnnees(data || []);
        // Pas de fixation ici : la sélection source/cible est gérée par les
        // deux effets dédiés ci-dessous (cible = active, source = l'autre).
      } catch {
        toast.error("Impossible de charger les années scolaires");
      }

      if (getTypesEleveFn) {
        try {
          const data = await getTypesEleveFn(slug);
          setTypesEleve(data?.results || data || []);
        } catch {
          setTypesEleve([]);
          toast.error("Impossible de charger les types d'élèves");
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // ─── PROPOSITION AUTOMATIQUE : CIBLE = ANNÉE ACTIVE ─────────────────────
  useEffect(() => {
    if (cibleModifieeManuellement) return;
    if (!annees.length) return;
    const active = annees.find(a => a.actif);
    setAnneeCibleId(active ? String(active.id) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annees, cibleModifieeManuellement]);

  // ─── PROPOSITION AUTOMATIQUE : SOURCE = L'AUTRE ANNÉE NON ARCHIVÉE ──────
  useEffect(() => {
    if (sourceModifieeManuellement) return;
    if (!annees.length || !anneeCibleId) return;
    const sourceProposee = annees.find(
      a => !a.archived && String(a.id) !== String(anneeCibleId)
    );
    setAnneeSourceId(sourceProposee ? String(sourceProposee.id) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anneeCibleId, annees, sourceModifieeManuellement]);

  const handleChangeSource = (val) => {
    setAnneeSourceId(val);
    setSourceModifieeManuellement(true);
  };

  const handleChangeCible = (val) => {
    setAnneeCibleId(val);
    setCibleModifieeManuellement(true);
  };

  // ─── CHARGEMENT DU TABLEAU DE BORD ──────────────────────────────────────
  useEffect(() => {
    if (!slug || !anneeSourceId || !anneeCibleId || anneeSourceId === anneeCibleId) {
      setDashboard(null);
      return;
    }
    (async () => {
      setDashboardLoading(true);
      try {
        const data = await getReinscriptionClasses(slug, { annee_source: anneeSourceId, annee_cible: anneeCibleId });
        setDashboard(data);
      } catch {
        toast.error("Impossible de charger le tableau de bord de réinscription");
      } finally {
        setDashboardLoading(false);
      }
    })();
    setClasseSource(null);
    setEleves([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, anneeSourceId, anneeCibleId]);

  // ─── CHARGEMENT DES CLASSES CIBLE (année cible) ─────────────────────────
  useEffect(() => {
    if (!slug || !anneeCibleId) return;
    getClasses(slug, { annee_scolaire: anneeCibleId, show_archived: 'false' })
      .then(data => setClassesCible(data || []))
      .catch(() => setClassesCible([]));
  }, [slug, anneeCibleId]);

  // ─── OUVRIR UNE CLASSE SOURCE ────────────────────────────────────────────
  const ouvrirClasse = async (classe) => {
    setClasseSource(classe);
    setElevesLoading(true);
    setLignes({});
    setClasseCibleGroupe("");
    setTypeEleveGroupe("");
    try {
      const data = await getReinscriptionElevesClasse(slug, classe.classe_id, { annee_cible: anneeCibleId });
      setEleves(data.eleves || []);
      const memeNom = classesCible.find(c => c.nom === classe.classe_nom);
      if (memeNom) setClasseCibleGroupe(String(memeNom.id));

      const initLignes = {};
      (data.eleves || []).forEach(e => {
        initLignes[e.eleve_id] = {
          // Aucun élève précoché par défaut — l'admin choisit explicitement
          // qui réinscrire, pour éviter tout traitement en masse accidentel.
          selectionne: false,
          classe_cible_id: memeNom ? String(memeNom.id) : "",
          // Pré-rempli avec le type d'élève de l'année source, si connu.
          type_eleve_id: e.type_eleve_id ? String(e.type_eleve_id) : "",
          is_redoublant: false,
          exclu: false,
        };
      });
      setLignes(initLignes);
    } catch {
      toast.error("Impossible de charger les élèves de cette classe");
    } finally {
      setElevesLoading(false);
    }
  };

  const elevesAffiches = useMemo(
    () => eleves.filter(e => afficherDejaTraites || !e.deja_reinscrit),
    [eleves, afficherDejaTraites]
  );

  const appliquerClasseGroupe = () => {
    if (!classeCibleGroupe) return;
    setLignes(prev => {
      const next = { ...prev };
      eleves.forEach(e => {
        if (e.deja_reinscrit || next[e.eleve_id]?.exclu) return;
        next[e.eleve_id] = { ...next[e.eleve_id], classe_cible_id: classeCibleGroupe };
      });
      return next;
    });
  };

  const appliquerTypeEleveGroupe = () => {
    if (!typeEleveGroupe) return;
    setLignes(prev => {
      const next = { ...prev };
      eleves.forEach(e => {
        if (e.deja_reinscrit || next[e.eleve_id]?.exclu) return;
        next[e.eleve_id] = { ...next[e.eleve_id], type_eleve_id: typeEleveGroupe };
      });
      return next;
    });
  };

  const setLigne = (eleveId, field, value) => {
    setLignes(prev => ({ ...prev, [eleveId]: { ...prev[eleveId], [field]: value } }));
  };

  const toggleTousSelectionnes = (val) => {
    setLignes(prev => {
      const next = { ...prev };
      elevesAffiches.forEach(e => {
        if (e.deja_reinscrit) return;
        next[e.eleve_id] = { ...next[e.eleve_id], selectionne: val };
      });
      return next;
    });
  };

  // ─── TOTAL DE SCOLARITÉ PAR LIGNE (classe cible + type élève) ────────────
  // Mise en cache par "classeId-typeId" pour éviter de refaire l'appel pour
  // chaque élève qui partage la même combinaison (cas très fréquent : tout
  // le groupe passe dans la même classe avec le même type).
  useEffect(() => {
    const combinaisons = new Set();
    Object.values(lignes).forEach(l => {
      if (l?.classe_cible_id && l?.type_eleve_id) {
        combinaisons.add(`${l.classe_cible_id}-${l.type_eleve_id}`);
      }
    });
    const aCalculer = [...combinaisons].filter(key => !(key in totauxCache));
    if (aCalculer.length === 0 || !getAffectationsFrais) return;

    let annule = false;
    (async () => {
      const nouveaux = {};
      for (const key of aCalculer) {
        const [classeId, typeId] = key.split("-");
        try {
          const affectations = await getAffectationsFrais(slug, { classe: classeId });
          const total = (affectations || [])
            .filter(a => String(a.type_eleve) === String(typeId))
            .reduce((s, a) => s + Number(a.montant || 0), 0);
          nouveaux[key] = total;
        } catch {
          nouveaux[key] = null;
        }
      }
      if (!annule) setTotauxCache(prev => ({ ...prev, ...nouveaux }));
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lignes]);

  const getTotalLigne = (ligne) => {
    if (!ligne?.classe_cible_id || !ligne?.type_eleve_id) return undefined;
    return totauxCache[`${ligne.classe_cible_id}-${ligne.type_eleve_id}`];
  };

  // ─── VALIDATION EN MASSE ─────────────────────────────────────────────────
  const handleReinscrire = async () => {
    const aTraiter = eleves
      .filter(e => !e.deja_reinscrit)
      .map(e => ({ eleve: e, ligne: lignes[e.eleve_id] }))
      .filter(({ ligne }) => ligne?.selectionne && !ligne.exclu);

    if (aTraiter.length === 0) {
      toast.error("Sélectionnez au moins un élève à réinscrire");
      return;
    }
    const sansClasse = aTraiter.find(({ ligne }) => !ligne.classe_cible_id);
    if (sansClasse) {
      toast.error("Choisissez une classe cible pour chaque élève sélectionné");
      return;
    }
    const sansType = aTraiter.find(({ ligne }) => !ligne.type_eleve_id);
    if (sansType) {
      toast.error(
        `Choisissez un type d'élève pour chaque élève sélectionné (ex: ${sansType.eleve.prenom} ${sansType.eleve.nom}) — il détermine les frais de scolarité`
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        annee_cible: parseInt(anneeCibleId),
        reinscriptions: aTraiter.map(({ eleve, ligne }) => ({
          eleve_id: eleve.eleve_id,
          classe_cible_id: parseInt(ligne.classe_cible_id),
          is_redoublant: !!ligne.is_redoublant,
          classe_redoublee_id: ligne.is_redoublant ? classeSource.classe_id : null,
          type_eleve_id: parseInt(ligne.type_eleve_id),
        })),
      };
      const res = await validerReinscription(slug, payload);
      toast.success(res.message || "Réinscription effectuée");

      await ouvrirClasse(classeSource);
      const dashData = await getReinscriptionClasses(slug, { annee_source: anneeSourceId, annee_cible: anneeCibleId });
      setDashboard(dashData);
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de la réinscription en masse");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "var(--m)", marginBottom: 10 }}>
          L'année cible (nouvelle année) est proposée automatiquement à partir de l'année <strong>active</strong>.
          L'année source (précédente) est proposée comme l'autre année non archivée. Vous pouvez
          ajuster manuellement si besoin.
        </div>
        <div className="sb2">
          <div className="fg" style={{ minWidth: 220 }}>
            <label>Année source (précédente)</label>
            <select value={anneeSourceId} onChange={e => handleChangeSource(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {annees.filter(a => String(a.id) !== String(anneeCibleId)).map(a => (
                <option key={a.id} value={a.id}>{a.nom} {a.actif ? "(Active)" : a.archived ? "(Archivée)" : ""}</option>
              ))}
            </select>
          </div>
          <div className="fg" style={{ minWidth: 220 }}>
            <label>Année cible (nouvelle année)</label>
            <select value={anneeCibleId} onChange={e => handleChangeCible(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {annees.filter(a => String(a.id) !== String(anneeSourceId)).map(a => (
                <option key={a.id} value={a.id}>{a.nom} {a.actif ? "(Active)" : ""}</option>
              ))}
            </select>
          </div>
        </div>
        {!anneeCibleId && (
          <div style={{ fontSize: 12, color: "var(--h)", marginTop: 6 }}>
            Aucune année active trouvée pour servir de cible. Activez d'abord la nouvelle année
            scolaire depuis l'onglet « Années scolaires ».
          </div>
        )}
        {typesEleve.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--rd)", marginTop: 6 }}>
            <i className="ti ti-alert-triangle" /> Aucun type d'élève configuré — allez dans
            Caisse → Configuration → Types d'élèves avant de réinscrire en masse.
          </div>
        )}
      </div>

      {dashboardLoading && <Loader />}

      {dashboard && !dashboardLoading && (
        <>
          <div className="card" style={{ padding: 14, marginBottom: 16, display: "flex", gap: 24, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {dashboard.global.deja_reinscrits} / {dashboard.global.total_eleves}
              </div>
              <div style={{ fontSize: 12, color: "var(--m)" }}>élèves réinscrits vers {dashboard.annee_cible.nom}</div>
            </div>
            <div style={{ flex: 1, height: 10, background: "var(--s2)", borderRadius: 6, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${dashboard.global.total_eleves ? (dashboard.global.deja_reinscrits / dashboard.global.total_eleves) * 100 : 0}%`,
                background: "#28a745",
              }} />
            </div>
            <div style={{ fontSize: 13, color: "var(--m)" }}>{dashboard.global.restants} restant(s)</div>
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {/* Colonne gauche : liste des classes source */}
            <div style={{ flex: "1 1 280px", minWidth: 260 }}>
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                {dashboard.classes.map(c => (
                  <div
                    key={c.classe_id}
                    onClick={() => ouvrirClasse(c)}
                    style={{
                      padding: "10px 14px", cursor: "pointer",
                      background: classeSource?.classe_id === c.classe_id ? "var(--bl-bg)" : "transparent",
                      borderBottom: "1px solid var(--b)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{c.classe_nom}</span>
                      {c.termine ? (
                        <span className="pill info"><i className="ti ti-circle-check" /> Terminé</span>
                      ) : (
                        <span className="pill pu">{c.restants} restant(s)</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--m)" }}>
                      {c.niveau_nom ? `${c.niveau_nom} · ` : ""}{c.deja_reinscrits} / {c.total_eleves} réinscrits
                    </div>
                    <div style={{ height: 5, background: "var(--s2)", borderRadius: 4, marginTop: 4, overflow: "hidden" }}>
                      <div style={{
                        height: "100%",
                        width: `${c.total_eleves ? (c.deja_reinscrits / c.total_eleves) * 100 : 0}%`,
                        background: c.termine ? "#28a745" : "#4a90e2",
                      }} />
                    </div>
                  </div>
                ))}
                {dashboard.classes.length === 0 && (
                  <div style={{ padding: 20, textAlign: "center", color: "var(--h)" }}>
                    Aucune classe active dans l'année source.
                  </div>
                )}
              </div>
            </div>

            {/* Colonne droite : détail de la classe sélectionnée */}
            <div style={{ flex: "2 1 480px", minWidth: 320, maxWidth: "100%" }}>
              {!classeSource ? (
                <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--h)" }}>
                  Sélectionnez une classe à gauche pour commencer la réinscription.
                </div>
              ) : elevesLoading ? (
                <Loader />
              ) : (
                <div className="card" style={{ padding: 16, maxWidth: "100%" }}>
                  <div style={{ fontWeight: 700, marginBottom: 10 }}>
                    {classeSource.classe_nom} — réinscription vers {dashboard.annee_cible.nom}
                  </div>

                  <div style={{ fontSize: 12, color: "var(--h)", marginBottom: 10 }}>
                    <i className="ti ti-info-circle" /> Cochez les élèves à réinscrire — aucun n'est présélectionné.
                  </div>

                  <div className="sb2" style={{ marginBottom: 6, flexWrap: "wrap" }}>
                    <select value={classeCibleGroupe} onChange={e => setClasseCibleGroupe(e.target.value)} style={{ minWidth: 190 }}>
                      <option value="">— Classe cible pour le groupe —</option>
                      {classesCible.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                    </select>
                    <button className="bsec" onClick={appliquerClasseGroupe} disabled={!classeCibleGroupe}>
                      Appliquer classe à tous
                    </button>

                    <select value={typeEleveGroupe} onChange={e => setTypeEleveGroupe(e.target.value)} style={{ minWidth: 190 }}>
                      <option value="">— Type d'élève pour le groupe —</option>
                      {typesEleve.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                    </select>
                    <button className="bsec" onClick={appliquerTypeEleveGroupe} disabled={!typeEleveGroupe}>
                      Appliquer type à tous
                    </button>
                  </div>
                  <div className="sb2" style={{ marginBottom: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginLeft: "auto" }}>
                      <input type="checkbox" checked={afficherDejaTraites} onChange={e => setAfficherDejaTraites(e.target.checked)} />
                      Afficher aussi les déjà réinscrits
                    </label>
                  </div>

                  {/* ─── TABLEAU — colonnes resserrées, selects bien cadrés ─── */}
                  <div className="tbl-scroll-wrap">
                    <table className="tbl tbl-reinscription">
                      <thead>
                        <tr>
                          <th className="col-check">
                            <input type="checkbox" onChange={e => toggleTousSelectionnes(e.target.checked)} />
                          </th>
                          <th className="col-eleve">Élève</th>
                          <th className="col-select">Classe cible</th>
                          <th className="col-select">Type élève</th>
                          <th className="col-check">Redoub.</th>
                          <th className="col-montant">Scolarité</th>
                          <th className="col-statut">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {elevesAffiches.map(e => {
                          const ligne = lignes[e.eleve_id] || {};
                          const total = getTotalLigne(ligne);
                          return (
                            <tr key={e.eleve_id} style={{ opacity: e.deja_reinscrit ? 0.6 : 1 }}>
                              <td className="col-check">
                                {!e.deja_reinscrit && (
                                  <input
                                    type="checkbox"
                                    checked={!!ligne.selectionne}
                                    onChange={ev => setLigne(e.eleve_id, 'selectionne', ev.target.checked)}
                                  />
                                )}
                              </td>
                              <td className="col-eleve">
                                <div className="eleve-cell">
                                  {e.photo ? (
                                    <img src={getImageUrl(e.photo)} alt="" className="eleve-photo" />
                                  ) : (
                                    <div className="eleve-photo eleve-photo-vide" />
                                  )}
                                  <div className="eleve-info">
                                    <div className="eleve-nom" title={`${e.prenom} ${e.nom}`}>{e.prenom} {e.nom}</div>
                                    <div className="eleve-matricule">{e.matricule}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="col-select">
                                {e.deja_reinscrit ? (
                                  <span className="valeur-figee">{e.classe_cible_actuelle}</span>
                                ) : (
                                  <select
                                    className="select-cell"
                                    value={ligne.classe_cible_id || ""}
                                    onChange={ev => setLigne(e.eleve_id, 'classe_cible_id', ev.target.value)}
                                    disabled={!ligne.selectionne}
                                  >
                                    <option value="">— Choisir —</option>
                                    {classesCible.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                                  </select>
                                )}
                              </td>
                              <td className="col-select">
                                {e.deja_reinscrit ? (
                                  <span className="valeur-figee">{e.type_eleve_libelle || "—"}</span>
                                ) : (
                                  <select
                                    className={`select-cell ${!ligne.type_eleve_id && ligne.selectionne ? "select-manquant" : ""}`}
                                    value={ligne.type_eleve_id || ""}
                                    onChange={ev => setLigne(e.eleve_id, 'type_eleve_id', ev.target.value)}
                                    disabled={!ligne.selectionne}
                                  >
                                    <option value="">— Choisir —</option>
                                    {typesEleve.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                                  </select>
                                )}
                              </td>
                              <td className="col-check">
                                {!e.deja_reinscrit && (
                                  <input
                                    type="checkbox"
                                    checked={!!ligne.is_redoublant}
                                    onChange={ev => setLigne(e.eleve_id, 'is_redoublant', ev.target.checked)}
                                    disabled={!ligne.selectionne}
                                  />
                                )}
                              </td>
                              <td className="col-montant">
                                {e.deja_reinscrit ? "—" : (
                                  total === undefined ? "—"
                                    : total === null ? <span className="valeur-figee">Erreur</span>
                                    : `${total.toLocaleString()} FCFA`
                                )}
                              </td>
                              <td className="col-statut">
                                {e.deja_reinscrit ? (
                                  <span className="pill info">Déjà réinscrit</span>
                                ) : (
                                  <span className="pill">En attente</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {elevesAffiches.length === 0 && (
                          <tr>
                            <td colSpan="7" style={{ textAlign: "center", color: "var(--h)", padding: 24 }}>
                              Tous les élèves de cette classe sont déjà réinscrits.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="factions">
                    <button className="bprim" onClick={handleReinscrire} disabled={saving}>
                      {saving
                        ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Réinscription en cours...</>
                        : <><i className="ti ti-check" /> Réinscrire la sélection</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

        .tbl-scroll-wrap {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border: 1px solid var(--b);
          border-radius: 8px;
        }

        table.tbl-reinscription {
          border: none;
          width: 100%;
          min-width: 760px;
          border-collapse: collapse;
          table-layout: fixed;
        }
        table.tbl-reinscription th,
        table.tbl-reinscription td {
          padding: 6px 8px;
          vertical-align: middle;
          box-sizing: border-box;
        }
        table.tbl-reinscription th {
          font-size: 12px;
          white-space: nowrap;
        }

        /* Colonnes serrées pour une simple case à cocher */
        .col-check { width: 46px; min-width: 46px; text-align: center; }

        /* Élève : assez large pour nom + matricule, sans excès */
        .col-eleve { width: 190px; min-width: 190px; }
        .eleve-cell { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .eleve-photo {
          width: 26px; height: 26px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
        }
        .eleve-photo-vide { background: var(--b); }
        .eleve-info { min-width: 0; overflow: hidden; }
        .eleve-nom {
          font-weight: 500; font-size: 13px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .eleve-matricule { font-size: 11px; color: var(--h); }

        /* Classe cible / Type élève : select cadré, bordure entièrement visible */
        .col-select { width: 160px; min-width: 160px; }
        .select-cell {
          width: 100%;
          box-sizing: border-box;
          padding: 4px 6px;
          font-size: 12px;
          border: 1px solid var(--b);
          border-radius: 6px;
          background: var(--s);
        }
        .select-manquant { border-color: var(--rd); }
        .valeur-figee { font-size: 12px; color: var(--m); white-space: nowrap; }

        /* Total scolarité */
        .col-montant { width: 110px; min-width: 110px; font-size: 12px; white-space: nowrap; }

        /* Statut */
        .col-statut { width: 110px; min-width: 110px; }
        .col-statut .pill { white-space: nowrap; }
      `}</style>
    </div>
  );
}